import React, { useState, useEffect } from 'react';
import { clientsApi, whatsappApi, bonusMessagesApi } from '../../utils/api';
import { supabase } from '../../utils/supabase';
import { Modal, Button, Descriptions, Divider, Tag, Spin, Form, Radio, InputNumber, Space, message } from 'antd';
import { UserOutlined, PhoneOutlined, MailOutlined, TagOutlined, ClockCircleOutlined, ShopOutlined, WhatsAppOutlined } from '@ant-design/icons';
import WhatsAppProviderBadge from '../whatsapp/WhatsAppProviderBadge';
import { getDeviseActive } from '../../utils/devise';

/**
 * Composant modal pour afficher les détails d'un client
 * @param {Object} props - Les propriétés du composant
 * @param {string} props.clientId - L'ID du client à afficher
 * @param {boolean} props.visible - Si le modal est visible
 * @param {Function} props.onClose - Fonction à appeler pour fermer le modal
 */
const ClientDetails = ({ clientId, visible, onClose, points }) => {
  // États pour stocker les données du client, l'état de chargement et les erreurs
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // États pour la fonctionnalité WhatsApp réduction
  const [whatsappModalVisible, setWhatsappModalVisible] = useState(false);
  const [reductionType, setReductionType] = useState('percentage'); // 'percentage' ou 'value'
  const [amountPerPoint, setAmountPerPoint] = useState(points);
  const [maxReduction, setMaxReduction] = useState(50); // Limite maximale de réduction par défaut
  const [sending, setSending] = useState(false);
  const [msgBonusTemplate, setMsgBonusTemplate] = useState(null);
  const [checkingRecent, setCheckingRecent] = useState(false);
  const [recentMessageWarning, setRecentMessageWarning] = useState(null);
  const [form] = Form.useForm();

  // Récupérer les détails du client lorsque le modal devient visible ou que l'ID change
  useEffect(() => {
    if (clientId && visible) {
      setLoading(true);
      setError(null);
      
      // Appeler l'API pour récupérer les détails du client
      clientsApi.getById(clientId)
        .then(response => {
          setClient(response.data);
          setLoading(false);
        })
        .catch(err => {
          console.error('Erreur lors de la récupération des détails du client:', err);
          setError('Impossible de charger les détails du client. Veuillez réessayer.');
          setLoading(false);
        });
    }
  }, [clientId, visible]);

  // Charger le template MSG_BONUS depuis la base de données
  const loadMsgBonusTemplate = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('command_types')
        .select('*')
        .eq('user_id', user.id)
        .eq('code', 'MSGBONUS')
        .single();

      if (error) {
        console.error('Erreur lors du chargement du template MSG_BONUS:', error);
        // Template par défaut si non trouvé
        setMsgBonusTemplate({
          message_template: 'Bonjour {client_name} ! 🎉 Grâce à vos {points} points, vous bénéficiez d\'une réduction de {reduction_amount} {reduction_type} ! Profitez-en lors de votre prochaine commande. Merci de votre fidélité ! 😊'
        });
      } else {
        setMsgBonusTemplate(data);
      }
    } catch (err) {
      console.error('Erreur lors du chargement du template:', err);
      // Template par défaut en cas d'erreur
      setMsgBonusTemplate({
        message_template: 'Bonjour {client_name} ! 🎉 Grâce à vos {points} points, vous bénéficiez d\'une réduction de {reduction_amount} {reduction_type} ! Profitez-en lors de votre prochaine commande. Merci de votre fidélité ! 😊'
      });
    }
  };

  // Charger le template MSG_BONUS quand le modal devient visible
  useEffect(() => {
    if (visible) {
      loadMsgBonusTemplate();
    }
  }, [visible]);

  // Formater la date au format local français
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Déterminer le statut de la liste noire
  const getBlacklistStatus = () => {
    if (!client) return null;
    
    return client.is_blacklisted ? (
      <Tag color="red">Oui - {client.blacklist_reason || 'Aucune raison spécifiée'}</Tag>
    ) : (
      <Tag color="green">Non</Tag>
    );
  };

  // Déterminer l'affichage des points
  const getPointsDisplay = () => {
    if (!client || !client.customer_points || client.customer_points.length === 0) {
      return points;
    }
    return client.customer_points[0]?.total_points || points;
  };

  // Calculer le montant de réduction basé sur les points avec limite maximale
  const calculateReduction = () => {
    const points = parseInt(getPointsDisplay());
    const totalReduction = points * amountPerPoint;
    // Appliquer la limite maximale de réduction
    return Math.min(totalReduction, maxReduction);
  };

  // Vérifier si le client peut recevoir un message WhatsApp
  const canSendWhatsApp = () => {
    return client && client.phone && parseInt(getPointsDisplay()) > 0;
  };

  // Générer le message à partir du template
  const generateMessage = () => {
    if (!msgBonusTemplate) return '';
    
    const clientPoints = getPointsDisplay();
    const reductionAmount = calculateReduction();
    const reductionLabel = reductionType === 'percentage' ? '%' : getDeviseActive();
    
    return msgBonusTemplate.message_template
      .replace('{client_name}', client?.name || 'Cher client')
      .replace('{points}', clientPoints)
      .replace('{reduction_amount}', reductionAmount.toFixed(reductionType === 'percentage' ? 1 : 0))
      .replace('{reduction_type}', reductionLabel);
  };

  // Vérifier si un message récent a été envoyé
  const checkRecentBonusMessage = async () => {
    if (!client?.id) return;

    try {
      setCheckingRecent(true);
      const response = await bonusMessagesApi.checkRecent(client.id, 24);
      
      if (response.data.success && response.data.data.hasRecentMessage) {
        const lastMessage = response.data.data.lastMessage;
        setRecentMessageWarning({
          sent_at: lastMessage.sent_at,
          reduction_amount: lastMessage.reduction_amount,
          reduction_type: lastMessage.reduction_type
        });
      } else {
        setRecentMessageWarning(null);
      }
    } catch (error) {
      console.error('Erreur lors de la vérification des messages récents:', error);
      // Ne pas bloquer l'utilisateur si la vérification échoue
      setRecentMessageWarning(null);
    } finally {
      setCheckingRecent(false);
    }
  };

  // Ouvrir le modal de configuration WhatsApp
  const openWhatsAppModal = async () => {
    setWhatsappModalVisible(true);
    form.setFieldsValue({
      reductionType: reductionType,
      amountPerPoint: amountPerPoint,
      maxReduction: maxReduction
    });

    // Vérifier s'il y a un message récent
    await checkRecentBonusMessage();
  };

  // Envoyer le message WhatsApp et enregistrer l'historique
  const sendWhatsAppMessage = async () => {
    try {
      setSending(true);
      
      const messageContent = generateMessage();
      const clientPoints = getPointsDisplay();
      const reductionAmount = calculateReduction();
      
      // Préparer les données pour l'historique
      const historyData = {
        customer_id: client?.id || null,
        customer_phone: client?.phone ,
        customer_name: client?.name || 'Client inconnu',
        reduction_type: reductionType,
        amount_per_point: amountPerPoint,
        max_reduction: maxReduction,
        points_used: parseInt(clientPoints),
        reduction_amount: reductionAmount,
        message_content: messageContent,
        whatsapp_phone: client?.phone
      };

      // Envoyer le message WhatsApp
      const response = await whatsappApi.sendMessage({
        phone: client?.phone,
        message: messageContent
      });

      if (response.status === 200) {
        // Enregistrer dans l'historique avec statut 'sent'
        try {
          await bonusMessagesApi.createHistory({
            ...historyData,
            status: 'sent'
          });
          console.log('Historique du message de bonus enregistré avec succès');
        } catch (historyError) {
          console.error('Erreur lors de l\'enregistrement de l\'historique:', historyError);
          // Ne pas bloquer l'utilisateur si l'historique échoue
        }

        message.success('Message WhatsApp envoyé avec succès !');
        setWhatsappModalVisible(false);
        setRecentMessageWarning(null); // Réinitialiser l'avertissement
      } else {
        throw new Error('Erreur lors de l\'envoi du message');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message WhatsApp:', error);
      
      // Enregistrer l'échec dans l'historique
      try {
        const historyData = {
          customer_id: client?.id || null,
          customer_phone: client?.phone ,
          customer_name: client?.name || 'Client inconnu',
          reduction_type: reductionType,
          amount_per_point: amountPerPoint,
          max_reduction: maxReduction,
          points_used: parseInt(getPointsDisplay()),
          reduction_amount: calculateReduction(),
          message_content: generateMessage(),
          whatsapp_phone: client?.phone,
          status: 'failed',
          error_message: error.message || 'Erreur inconnue'
        };

        await bonusMessagesApi.createHistory(historyData);
        console.log('Historique de l\'échec enregistré');
      } catch (historyError) {
        console.error('Erreur lors de l\'enregistrement de l\'historique d\'échec:', historyError);
      }

      message.error('Erreur lors de l\'envoi du message WhatsApp. Veuillez réessayer.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      title={<span><UserOutlined /> Détails du Client</span>}
      open={visible}
      onCancel={onClose}
      footer={[
        canSendWhatsApp() && (
          <Button 
            key="whatsapp" 
            type="primary" 
            icon={<WhatsAppOutlined />}
            onClick={openWhatsAppModal}
            style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
            size={window.innerWidth < 768 ? 'small' : 'middle'}
            block={window.innerWidth < 768}
          >
            {window.innerWidth < 768 ? 'WhatsApp Réduction' : 'Envoyer Réduction WhatsApp'}
          </Button>
        ),
        <Button 
          key="close" 
          onClick={onClose}
          size={window.innerWidth < 768 ? 'small' : 'middle'}
          block={window.innerWidth < 768}
        >
          Fermer
        </Button>
      ].filter(Boolean)}
      width={window.innerWidth < 768 ? '95vw' : 700}
      centered={window.innerWidth < 768}
    >
      {loading ? (
        <div className="text-center py-6 sm:py-8">
          <Spin size={window.innerWidth < 768 ? 'default' : 'large'} />
          <p className={`mt-2 ${window.innerWidth < 768 ? 'text-sm' : 'text-base'}`}>
            Chargement des détails...
          </p>
        </div>
      ) : error ? (
        <div className={`text-center text-red-500 py-4 sm:py-5 ${
          window.innerWidth < 768 ? 'text-sm' : 'text-base'
        }`}>
          {error}
        </div>
      ) : client ? (
        <div>
          <Descriptions 
            bordered 
            column={window.innerWidth < 768 ? 1 : 2}
            size={window.innerWidth < 768 ? 'small' : 'default'}
          >
            <Descriptions.Item label="Nom" span={window.innerWidth < 768 ? 1 : 2}>
              <strong className={window.innerWidth < 768 ? 'text-sm' : ''}>
                {client.name || 'Non spécifié'}
              </strong>
            </Descriptions.Item>
            
            <Descriptions.Item label={<span><PhoneOutlined /> Téléphone</span>}>
              <span className={window.innerWidth < 768 ? 'text-sm' : ''}>
                {client.phone || 'Non spécifié'}
              </span>
            </Descriptions.Item>
            
            <Descriptions.Item label={<span><MailOutlined /> Email</span>}>
              <span className={`${window.innerWidth < 768 ? 'text-sm break-all' : ''}`}>
                {client.email || 'Non spécifié'}
              </span>
            </Descriptions.Item>
            
            <Descriptions.Item label={<span><ShopOutlined /> Source</span>}>
              <span className={window.innerWidth < 768 ? 'text-sm' : ''}>
                {client.source || 'Non spécifiée'}
              </span>
            </Descriptions.Item>
            
            <Descriptions.Item label={<span><TagOutlined /> Points</span>}>
              <span className={`${window.innerWidth < 768 ? 'text-sm font-semibold' : 'font-semibold'}`}>
                {getPointsDisplay()}
              </span>
            </Descriptions.Item>
            
            <Descriptions.Item label="Liste noire" span={window.innerWidth < 768 ? 1 : 2}>
              {getBlacklistStatus()}
            </Descriptions.Item>
            
            <Descriptions.Item label={<span><ClockCircleOutlined /> Créé le</span>}>
              <span className={window.innerWidth < 768 ? 'text-xs' : 'text-sm'}>
                {formatDate(client.created_at)}
              </span>
            </Descriptions.Item>
            
            <Descriptions.Item label="Dernière modification">
              <span className={window.innerWidth < 768 ? 'text-xs' : 'text-sm'}>
                {formatDate(client.updated_at)}
              </span>
            </Descriptions.Item>
          </Descriptions>
          
          {client.customer_addresses && client.customer_addresses.length > 0 && (
            <>
              <Divider orientation="left">
                <span className={window.innerWidth < 768 ? 'text-sm' : ''}>
                  Adresses
                </span>
              </Divider>
              {client.customer_addresses.map((address, index) => (
                <Descriptions 
                  key={address.id || index} 
                  bordered 
                  size="small" 
                  column={window.innerWidth < 768 ? 1 : 3}
                  className="mb-4"
                >
                  <Descriptions.Item label="Titre" span={window.innerWidth < 768 ? 1 : 3}>
                    <span className={window.innerWidth < 768 ? 'text-sm font-medium' : ''}>
                      {address.title || `Adresse ${index + 1}`}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Adresse complète" span={window.innerWidth < 768 ? 1 : 3}>
                    <span className={window.innerWidth < 768 ? 'text-sm break-words' : ''}>
                      {address.address || 'Non spécifiée'}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Ville">
                    <span className={window.innerWidth < 768 ? 'text-sm' : ''}>
                      {address.city || 'Non spécifiée'}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Pays">
                    <span className={window.innerWidth < 768 ? 'text-sm' : ''}>
                      {address.country || 'Non spécifié'}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Par défaut">
                    {address.is_default ? (
                      <Tag color="blue" size={window.innerWidth < 768 ? 'small' : 'default'} className="private-tag-accent" >
                        Adresse par défaut
                      </Tag>
                    ) : (
                      <span className={window.innerWidth < 768 ? 'text-sm' : ''}>Non</span>
                    )}
                  </Descriptions.Item>
                </Descriptions>
              ))}
            </>
          )}
          
          <Divider orientation="left">
            <span className={window.innerWidth < 768 ? 'text-sm' : ''}>
              Autres informations
            </span>
          </Divider>
          <Descriptions 
            bordered 
            size="small"
            column={1}
          >
            <Descriptions.Item label="Notes">
              <span className={`${window.innerWidth < 768 ? 'text-sm break-words' : ''}`}>
                {client.notes || 'Aucune note'}
              </span>
            </Descriptions.Item>
          </Descriptions>
        </div>
      ) : (
        <div className={`text-center py-4 sm:py-5 ${
          window.innerWidth < 768 ? 'text-sm' : 'text-base'
        }`}>
          Aucune information client trouvée.
        </div>
      )}

      {/* Modal de configuration WhatsApp */}
      <Modal
        title={<Space><WhatsAppOutlined style={{ color: '#25D366' }} /> Configuration de Réduction WhatsApp <WhatsAppProviderBadge /></Space>}
        open={whatsappModalVisible}
        onCancel={() => setWhatsappModalVisible(false)}
        footer={[
          <Button 
            key="cancel" 
            onClick={() => setWhatsappModalVisible(false)}
            size={window.innerWidth < 768 ? 'small' : 'middle'}
            block={window.innerWidth < 768}
          >
            Annuler
          </Button>,
          <Button 
            key="send" 
            type="primary" 
            loading={sending}
            onClick={sendWhatsAppMessage}
            style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
            size={window.innerWidth < 768 ? 'small' : 'middle'}
            block={window.innerWidth < 768}
          >
            {window.innerWidth < 768 ? 'Envoyer' : 'Envoyer Message'}
          </Button>
        ]}
        width={window.innerWidth < 768 ? '95vw' : 600}
        centered={window.innerWidth < 768}
      >
        {/* Avertissement pour les messages récents */}
        {checkingRecent && (
          <div className={`mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-center ${
            window.innerWidth < 768 ? 'text-sm' : ''
          }`}>
            <Spin size="small" className="mr-2" />
            <span>Vérification des messages récents...</span>
          </div>
        )}

        {recentMessageWarning && (
          <div className={`mb-4 p-3 bg-orange-50 border border-orange-200 rounded-md ${
            window.innerWidth < 768 ? 'text-sm' : ''
          }`}>
            <div className="font-bold text-orange-800 mb-1">
              ⚠️ Message de bonus récent détecté
            </div>
            <div className={`text-orange-700 ${window.innerWidth < 768 ? 'text-xs' : 'text-sm'}`}>
              Un message de bonus de {recentMessageWarning.reduction_amount.toFixed(recentMessageWarning.reduction_type === 'percentage' ? 1 : 0)} {recentMessageWarning.reduction_type === 'percentage' ? '%' : getDeviseActive()} a été envoyé le {new Date(recentMessageWarning.sent_at).toLocaleDateString('fr-FR', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}.
            </div>
            <div className={`text-orange-600 italic mt-1 ${
              window.innerWidth < 768 ? 'text-xs' : 'text-sm'
            }`}>
              Considérez d'attendre avant d'envoyer un nouveau message pour éviter le spam.
            </div>
          </div>
        )}

        <Form
          form={form}
          layout="vertical"
          onValuesChange={(changedValues) => {
            if (changedValues.reductionType) {
              setReductionType(changedValues.reductionType);
            }
            if (changedValues.amountPerPoint !== undefined) {
              setAmountPerPoint(changedValues.amountPerPoint);
            }
            if (changedValues.maxReduction !== undefined) {
              setMaxReduction(changedValues.maxReduction);
            }
          }}
        >
          <Form.Item
            label="Type de réduction"
            name="reductionType"
            rules={[{ required: true, message: 'Veuillez sélectionner le type de réduction' }]}
          >
            <Radio.Group className={window.innerWidth < 768 ? 'flex flex-col gap-2' : ''}>
              <Radio value="percentage" className={window.innerWidth < 768 ? 'text-sm' : ''}>
                Pourcentage (%)
              </Radio>
              <Radio value="value" className={window.innerWidth < 768 ? 'text-sm' : ''}>
                Valeur fixe ({getDeviseActive()})
              </Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={`Montant par point (${reductionType === 'percentage' ? '%' : getDeviseActive()})`}
            name="amountPerPoint"
            rules={[
              { required: true, message: 'Veuillez saisir le montant par point' },
              { type: 'number', min: 0.01, message: 'Le montant doit être supérieur à 0' }
            ]}
          >
            <InputNumber
              className="w-full"
              placeholder={window.innerWidth < 768 ? (
                reductionType === 'percentage' ? 'Ex: 0.5' : 'Ex: 100'
              ) : (
                reductionType === 'percentage' ? 'Ex: 0.5 (0.5% par point)' : `Ex: 100 (100 ${getDeviseActive()} par point)`
              )}
              step={reductionType === 'percentage' ? 0.1 : 10}
              min={0.01}
              size={window.innerWidth < 768 ? 'small' : 'middle'}
            />
          </Form.Item>

          <Form.Item
            label={`Réduction maximale (${reductionType === 'percentage' ? '%' : getDeviseActive()})`}
            name="maxReduction"
            rules={[
              { required: true, message: 'Veuillez saisir la réduction maximale' },
              { type: 'number', min: 1, message: 'La réduction maximale doit être supérieure à 0' }
            ]}
            tooltip={window.innerWidth < 768 ? "Limite max" : "Cette limite empêche d'accorder des réductions excessives"}
          >
            <InputNumber
              className="w-full"
              placeholder={window.innerWidth < 768 ? (
                reductionType === 'percentage' ? 'Ex: 50' : 'Ex: 10000'
              ) : (
                reductionType === 'percentage' ? 'Ex: 50 (50% maximum)' : `Ex: 10000 (10000 ${getDeviseActive()} maximum)`
              )}
              step={reductionType === 'percentage' ? 5 : 1000}
              min={1}
              size={window.innerWidth < 768 ? 'small' : 'middle'}
            />
          </Form.Item>

          <Divider orientation="left">
            <span className={window.innerWidth < 768 ? 'text-sm' : ''}>
              Aperçu du calcul
            </span>
          </Divider>
          <div className={`bg-blue-50 p-4 rounded-lg mb-4 ${
            window.innerWidth < 768 ? 'text-sm' : ''
          }`}>
            <Space direction="vertical" className="w-full" size={window.innerWidth < 768 ? 'small' : 'middle'}>
              <div><strong>Points du client :</strong> {getPointsDisplay()}</div>
              <div><strong>Montant par point :</strong> {amountPerPoint} {reductionType === 'percentage' ? '%' : getDeviseActive()}</div>
              <div><strong>Calcul brut :</strong> {(parseInt(getPointsDisplay()) * amountPerPoint).toFixed(reductionType === 'percentage' ? 1 : 0)} {reductionType === 'percentage' ? '%' : getDeviseActive()}</div>
              <div><strong>Limite maximale :</strong> {maxReduction} {reductionType === 'percentage' ? '%' : getDeviseActive()}</div>
              <div className={`font-bold ${
                calculateReduction() < (parseInt(getPointsDisplay()) * amountPerPoint) ? 'text-red-500' : 'text-green-600'
              }`}>
                <strong>Réduction finale :</strong> {calculateReduction().toFixed(reductionType === 'percentage' ? 1 : 0)} {reductionType === 'percentage' ? '%' : getDeviseActive()}
                {calculateReduction() < (parseInt(getPointsDisplay()) * amountPerPoint) && (
                  <span className={`ml-2 ${window.innerWidth < 768 ? 'text-xs' : 'text-sm'}`}>
                    ⚠️ Limitée
                  </span>
                )}
              </div>
            </Space>
          </div>

          <Divider orientation="left">
            <span className={window.innerWidth < 768 ? 'text-sm' : ''}>
              Aperçu du message
            </span>
          </Divider>
          <div className={`bg-green-50 border border-green-200 p-4 rounded-lg ${
            window.innerWidth < 768 ? 'text-sm' : ''
          }`}>
            <div className="whitespace-pre-wrap italic break-words">
              {generateMessage()}
            </div>
          </div>
        </Form>
      </Modal>
    </Modal>
  );
};

export default ClientDetails;
